import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  LayoutDashboard, 
  CreditCard, 
  Users, 
  Settings, 
  BarChart3, 
  Store,
  Wallet,
  UserCheck,
  HelpCircle,
  LogOut,
  Upload,
  FileText,
  Lightbulb
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { APP_VERSION, fetchVersion } from '../../version';
import { subscribeSidebarCounts } from '../../lib/sidebar-counts';

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const { token, selectedPlatform, user, backendUrl, logout } = useAuth();
  
  // Dynamic API URL for dev/prod environments
  const API = `${backendUrl}/api`;
  const [transactionCount, setTransactionCount] = useState(0);
  const [insightCount, setInsightCount] = useState(0);
  const [customerCount, setCustomerCount] = useState(0);
  const [merchantCount, setMerchantCount] = useState(0);
  const [appVersion, setAppVersion] = useState(APP_VERSION);
  
  // Fetch version from backend API
  useEffect(() => {
    const loadVersion = async () => {
      if (backendUrl && token) {
        const version = await fetchVersion(backendUrl, token);
        setAppVersion(version);
      }
    };
    loadVersion();
  }, [backendUrl, token]);
  
  // Fetch all platform totals in ONE call (transactions, customers, merchants)
  // from /api/platform-stats. These are all-time totals and don't change on
  // filter changes elsewhere in the app.
  const fetchPlatformTotals = async () => {
    if (!token || !selectedPlatform) return;
    try {
      const response = await axios.get(
        `${backendUrl}/api/platform-stats?platform_id=${encodeURIComponent(selectedPlatform)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const totals = response.data?.totals || {};
      setTransactionCount(totals.transactions || 0);
      setCustomerCount(totals.customers || 0);
      setMerchantCount(totals.merchants || 0);
    } catch (err) {
      console.error('Error fetching platform totals:', err);
      setTransactionCount(0);
      setCustomerCount(0);
      setMerchantCount(0);
    }
  };

  // Fetch insight count (new + in_progress) — kept separate because
  // /api/platform-stats doesn't track insight status counts.
  const fetchInsightCount = async () => {
    if (!token || !selectedPlatform) return;
    try {
      const [newRes, inProgressRes] = await Promise.all([
        axios.get(`${backendUrl}/api/insights?page=1&per_page=1&platform_id=${encodeURIComponent(selectedPlatform)}&status=new`, {
          headers: { Authorization: `Bearer ${token}`, 'X-Portal-Background': '1' }
        }),
        axios.get(`${backendUrl}/api/insights?page=1&per_page=1&platform_id=${encodeURIComponent(selectedPlatform)}&status=in_progress`, {
          headers: { Authorization: `Bearer ${token}`, 'X-Portal-Background': '1' }
        }),
      ]);
      const total = (newRes.data.total || 0) + (inProgressRes.data.total || 0);
      setInsightCount(total);
    } catch (err) {
      console.error('Error fetching insight count:', err);
      setInsightCount(0);
    }
  };

  // Update counts when platform changes
  useEffect(() => {
    fetchPlatformTotals();
    fetchInsightCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedPlatform]);

  // Rejected (401/403) API calls NOT yet displayed by this admin — red badge on Audit Logs
  const [rejectedCount, setRejectedCount] = useState(0);
  useEffect(() => {
    if (!token || !user?.is_admin) return;
    let cancelled = false;
    const load = () => axios.get(`${backendUrl}/api/audit/rejected-requests/summary`, {
      headers: { Authorization: `Bearer ${token}`, 'X-Portal-Background': '1' }
    }).then((r) => { if (!cancelled) setRejectedCount(r.data?.unseen ?? 0); }).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    const onSeen = () => setRejectedCount(0);
    window.addEventListener('rejected-requests-seen', onSeen);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('rejected-requests-seen', onSeen); };
  }, [token, backendUrl, user?.is_admin]);

  // Insights still listen to live override events (the AI Insights page emits
  // counts to keep the sidebar in sync as filters change there). Transactions/
  // customers/merchants totals are intentionally all-time and ignore live
  // events to stay stable across the app.
  useEffect(() => {
    const unsub = subscribeSidebarCounts(({ entity, count }) => {
      if (entity === 'insights') setInsightCount(count);
    });
    return unsub;
  }, []);

  const navigation = [
    {
      name: 'Overview',
      href: '/dashboard',
      icon: LayoutDashboard,
      current: location.pathname === '/dashboard'
    },
    {
      name: 'AI Insights',
      href: '/dashboard/ai-insights',
      icon: Lightbulb,
      current: location.pathname === '/dashboard/ai-insights',
      badge: insightCount > 0 ? insightCount.toString() : undefined
    },
    {
      name: 'Analytics',
      href: '/dashboard/analytics',
      icon: BarChart3,
      current: location.pathname === '/dashboard/analytics'
    },
    {
      name: 'Transactions',
      href: '/dashboard/transactions',
      icon: CreditCard,
      current: location.pathname === '/dashboard/transactions' || location.pathname === '/dashboard/transactions-cards',
      badge: transactionCount > 0 ? transactionCount.toString() : undefined
    },
    {
      name: 'Customers',
      href: '/dashboard/customers',
      icon: Users,
      current: location.pathname === '/dashboard/customers',
      badge: customerCount > 0 ? customerCount.toString() : undefined
    },
    {
      name: 'Merchants',
      href: '/dashboard/merchants',
      icon: Store,
      current: location.pathname === '/dashboard/merchants',
      badge: merchantCount > 0 ? merchantCount.toString() : undefined
    },
    {
      name: 'Users',
      href: '/dashboard/users',
      icon: UserCheck,
      current: location.pathname === '/dashboard/users',
      adminOnly: true
    },
    {
      name: 'Excel Upload',
      href: '/dashboard/excel-upload',
      icon: Upload,
      current: location.pathname === '/dashboard/excel-upload',
      adminOnly: true
    },
    // {
    //   name: 'Payment Methods',
    //   href: '/dashboard/payment-methods',
    //   icon: Wallet,
    //   current: location.pathname === '/dashboard/payment-methods'
    // },
    {
      name: 'Audit Logs',
      href: '/dashboard/audit-logs',
      icon: FileText,
      current: location.pathname === '/dashboard/audit-logs',
      adminOnly: true,
      badge: rejectedCount > 0 ? rejectedCount.toString() : undefined,
      badgeAlert: rejectedCount > 0
    },
    {
      name: 'Settings',
      href: '/dashboard/settings',
      icon: Settings,
      current: location.pathname === '/dashboard/settings',
      adminOnly: true
    }
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={onClose} />
      )}
      
      <div 
        className={cn(
          "fixed inset-y-0 left-0 z-50 theme-sidebar theme-border transform transition-all duration-300 ease-in-out",
          // Mobile behavior
          "lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          // Desktop collapsed behavior
          isCollapsed ? "lg:w-16" : "lg:w-64",
          // Mobile always full width
          "w-64"
        )}
        style={{ backgroundColor: 'var(--sidebar-color)' }}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 px-4 border-b theme-border">
            <div className={cn(
              "flex items-center transition-all duration-300",
              isCollapsed ? "justify-center" : "space-x-3"
            )}>
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              {!isCollapsed && (
                <span className="text-xl font-bold text-white whitespace-nowrap overflow-hidden">
                  PayGateway Pro
                </span>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className={cn(
            "flex-1 py-6 space-y-1 overflow-y-auto",
            isCollapsed ? "px-2" : "px-4"
          )}>
            {navigation
              .filter(item => !item.adminOnly || user?.is_admin)
              .map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "sidebar-nav-item flex items-center text-sm font-medium rounded-md transition-all duration-200 group relative",
                    isCollapsed ? "justify-center px-2 py-3" : "justify-between px-3 py-2",
                    isActive
                      ? "active bg-blue-600"
                      : "hover:bg-blue-600/20"
                  )}
                  title={isCollapsed ? item.name : ""}
                >
                  <div className={cn(
                    "flex items-center",
                    isCollapsed ? "justify-center" : "space-x-3"
                  )}>
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {!isCollapsed && <span className="truncate">{item.name}</span>}
                  </div>
                  
                  {/* Tooltip for collapsed state */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                      {item.name}
                      {item.badge && (
                        <span className={`ml-2 px-1.5 py-0.5 text-xs rounded text-white font-semibold ${item.badgeAlert ? 'bg-red-600' : 'theme-button-primary'}`}>
                          {item.badge}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Badge for expanded state */}
                  {!isCollapsed && item.badge && (
                    <span data-testid={item.badgeAlert ? 'sidebar-rejected-badge' : undefined} className={`ml-2 flex-shrink-0 px-2.5 py-0.5 rounded-md text-xs font-semibold text-white ${item.badgeAlert ? 'bg-red-600' : 'theme-button-primary'}`} title={item.badgeAlert ? 'New rejected API requests since you last viewed them' : undefined}>
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Version Number */}
          <div className={cn(
            "py-3 text-center",
            isCollapsed ? "px-2" : "px-4"
          )}>
            <span className={cn(
              "text-xs text-gray-400",
              isCollapsed ? "text-[10px]" : ""
            )}>
              {isCollapsed ? `v${appVersion}` : `Version ${appVersion}`}
            </span>
          </div>

          {/* Bottom section */}
          <div className={cn(
            "py-4 border-t border-gray-200",
            isCollapsed ? "px-2" : "px-4"
          )}>
            <div className="space-y-2">
              <Button 
                variant="ghost" 
                className={cn(
                  "group relative",
                  isCollapsed ? "w-12 h-12 p-0 justify-center" : "w-full justify-start"
                )}
                title={isCollapsed ? "Help & Support" : ""}
              >
                <HelpCircle className="h-4 w-4" />
                {!isCollapsed && <span className="ml-3">Help & Support</span>}
                
                {/* Tooltip for collapsed state */}
                {isCollapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                    Help & Support
                  </div>
                )}
              </Button>
              
              <Button 
                variant="ghost" 
                className={cn(
                  "text-red-600 hover:text-red-700 group relative",
                  isCollapsed ? "w-12 h-12 p-0 justify-center" : "w-full justify-start"
                )}
                title={isCollapsed ? "Sign Out" : ""}
                onClick={() => logout()}
              >
                <LogOut className="h-4 w-4" />
                {!isCollapsed && <span className="ml-3">Sign Out</span>}
                
                {/* Tooltip for collapsed state */}
                {isCollapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                    Sign Out
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;